import React, { useEffect, useRef, useState } from "react";
import { ModuloCompila } from "./modulo/ModuloCompila";
import { SpeedTestPanel } from "./SpeedTestPanel";
import { api, API, formatApiError } from "../lib/api";
import UploadZone from "./UploadZone";
import RichTextEditor from "./RichTextEditor";
import { FileText, Image as ImageIcon, Trash2, Save, Loader2, X, Download, Eye, CheckCircle2, Circle, RotateCcw, UserCheck, Lock, GripVertical, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PhotoAnnotator from "./PhotoAnnotator";
import { usePhotoActions } from "./PhotoActions";
import { initialNotesFor } from "./noteTemplates";
import { DicoAssistenteButton } from "./DicoAssistente";

export default function SectionBlock({ cid, module, section, initialNotes, initialFiles, initialCompleted, initialCompletedBy, initialCompletedAt, onChanged, readOnly = false }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState(() => initialNotesFor(module, section.slug, initialNotes));
  const [files, setFiles] = useState(initialFiles || []);
  const [completed, setCompleted] = useState(!!initialCompleted);
  const [completedBy, setCompletedBy] = useState(initialCompletedBy || "");
  const [completedAt, setCompletedAt] = useState(initialCompletedAt || "");
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null); // {src, name}
  const [annotating, setAnnotating] = useState(null); // file doc

  useEffect(() => { setNotes(initialNotesFor(module, section.slug, initialNotes)); }, [initialNotes]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setFiles(initialFiles || []); }, [initialFiles]);
  useEffect(() => { setCompleted(!!initialCompleted); }, [initialCompleted]);
  useEffect(() => { setCompletedBy(initialCompletedBy || ""); }, [initialCompletedBy]);
  useEffect(() => { setCompletedAt(initialCompletedAt || ""); }, [initialCompletedAt]);

  const isAdmin = (user?.role === "admin");
  // Lock applicato a TUTTI (anche admin): per modificare serve sbloccare prima la sezione.
  // readOnly (es. sopralluogo per supervisore/responsabile): sola visualizzazione totale.
  const locked = completed || readOnly;

  const toggleCompleted = async () => {
    const next = !completed;
    setToggling(true);
    try {
      await api.put(`/clienti/${cid}/${module}/sections/${section.slug}/complete`, { completed: next });
      setCompleted(next);
      if (next) {
        // optimistic local audit (verrà comunque rinfrescato via onChanged → loadAll)
        setCompletedBy(user?.name || user?.email || "Tu");
        setCompletedAt(new Date().toISOString());
        toast.success(`${section.label}: completata`);
      } else {
        setCompletedBy("");
        setCompletedAt("");
        toast.success(`${section.label}: sbloccata`);
      }
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setToggling(false);
    }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      await api.put(`/clienti/${cid}/${module}/sections/${section.slug}`, { notes });
      dirty.current = false;
      toast.success(`Note salvate: ${section.label}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // Salvataggio automatico delle note mentre si scrive (così l'assistente DiCo legge sempre quanto scritto)
  const dirty = useRef(false);
  const notesRef = useRef(notes);
  notesRef.current = notes;
  const [autoState, setAutoState] = useState("");
  const onNotesChange = (v) => { setNotes(v); dirty.current = true; setAutoState("Modifiche in corso…"); };
  const putNotes = (v) => api.put(`/clienti/${cid}/${module}/sections/${section.slug}`, { notes: v });
  useEffect(() => {
    if (!dirty.current || locked || readOnly) return undefined;
    const t = setTimeout(() => {
      putNotes(notes).then(() => { dirty.current = false; setAutoState("Salvato automaticamente"); }).catch(() => setAutoState("Salvataggio automatico non riuscito"));
    }, 1500);
    return () => clearTimeout(t);
  }, [notes]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (dirty.current) putNotes(notesRef.current).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async (fs) => {
    for (const f of fs) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("section", section.slug);
      try {
        const { data } = await api.post(`/clienti/${cid}/${module}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFiles((prev) => [...prev, data]);
      } catch (err) {
        toast.error(`${f.name}: ${formatApiError(err)}`);
      }
    }
    toast.success("File caricati");
    onChanged?.();
  };

  const removeFile = async (f) => {
    if (!window.confirm(`Eliminare ${f.original_filename}?`)) return;
    try {
      await api.delete(`/files/${f.id}`);
      setFiles(files.filter((x) => x.id !== f.id));
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const dragIndex = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const persistOrder = async (ordered) => {
    const prev = files;
    setFiles(ordered);
    try {
      await api.patch(`/clienti/${cid}/${module}/files/reorder`, {
        section: section.slug,
        file_ids: ordered.map((f) => f.id),
      });
      toast.success("Ordine aggiornato");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
      setFiles(prev);
    }
  };

  const handleDrop = (toIdx) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    setDragOver(null);
    if (from === null || from === toIdx) return;
    const next = [...files];
    const [moved] = next.splice(from, 1);
    next.splice(toIdx, 0, moved);
    persistOrder(next);
  };

  const tokenParam = () => {
    const t = localStorage.getItem("access_token");
    return t ? `?auth=${encodeURIComponent(t)}` : "";
  };
  const srcOf = (f) => {
    const t = tokenParam();
    return `${API}/files/${f.id}/download${t}${t ? "&" : "?"}v=${f.size || 0}`;
  };
  const photos = usePhotoActions({
    items: files.filter((f) => (f.content_type || "").startsWith("image/")).map((f) => ({ f, src: srcOf(f) })),
    canEdit: !locked, onEdit: setAnnotating, onDelete: removeFile,
  });

  return (
    <div
      className="card-shell p-5 transition-colors"
      style={completed ? { borderColor: "#10B981", boxShadow: "inset 0 0 0 1px #10B981" } : undefined}
      data-testid={`section-${section.slug}`}
      data-completed={completed ? "true" : "false"}
    >
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="font-bold text-base truncate" style={{ fontFamily: "Manrope" }}>{section.label}</h3>
          {completed && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full"
              style={{ background: "#D1FAE5", color: "#065F46" }}
              data-testid={`section-completed-badge-${section.slug}`}
            >
              <CheckCircle2 className="w-3 h-3" /> Completata
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>{files.length} file</span>
          {/* Toggle switch nell'header */}
          {!readOnly && (
          <button
            type="button"
            onClick={toggleCompleted}
            disabled={toggling}
            role="switch"
            aria-checked={completed}
            title={completed ? "Sblocca sezione" : "Segna come completata"}
            data-testid={`toggle-complete-${section.slug}`}
            className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--primary)] disabled:opacity-50"
            style={{ background: completed ? "#10B981" : "#D1D5DB" }}
          >
            <span
              className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform"
              style={{ transform: completed ? "translateX(24px)" : "translateX(4px)" }}
            />
          </button>
          )}
        </div>
      </div>

      {isAdmin && completed && completedBy && (
        <div
          className="mb-3 inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-md"
          style={{ background: "#ECFDF5", color: "#065F46" }}
          data-testid={`section-completed-meta-${section.slug}`}
        >
          <UserCheck className="w-3 h-3" />
          Completata da <b>{completedBy}</b>
          {completedAt && <> il <b>{new Date(completedAt).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })}</b></>}
        </div>
      )}

      {locked && !readOnly && (
        <div
          className="mb-3 flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-md border"
          style={{ background: "#FFFBEB", color: "#92400E", borderColor: "#FDE68A" }}
          data-testid={`section-locked-banner-${section.slug}`}
        >
          <Lock className="w-3.5 h-3.5 shrink-0" />
          Sezione completata e bloccata. Clicca "Sblocca sezione" per poter modificare.
        </div>
      )}
      {readOnly && (
        <div
          className="mb-3 flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-md border"
          style={{ background: "var(--surface-muted)", color: "var(--text-muted)", borderColor: "var(--border)" }}
          data-testid={`section-readonly-banner-${section.slug}`}
        >
          <Eye className="w-3.5 h-3.5 shrink-0" />
          Sola visualizzazione
        </div>
      )}

      {module === "sopralluogo" && section.slug === "modulo_sopralluogo" && <ModuloCompila cid={cid} onChanged={onChanged} locked={locked} />}

      {module === "sopralluogo" && section.slug === "speed_test_wifi" && !locked && (
        <SpeedTestPanel onResult={async (file, r) => {
          const line = `<p><b>Speed test</b> ${new Date().toLocaleString("it-IT")}: download ${r.download.toFixed(1)} Mbps · upload ${r.upload.toFixed(1)} Mbps · ping ${Math.round(r.ping)} ms</p>`;
          const next = `${notes || ""}${line}`;
          setNotes(next);
          await api.put(`/clienti/${cid}/${module}/sections/${section.slug}`, { notes: next }).catch(() => {});
          await handleUpload([file]);
        }} />
      )}

      <RichTextEditor
        value={notes}
        onChange={onNotesChange}
        disabled={locked}
        testid={`notes-${section.slug}`}
        placeholder={`Note e dettagli di ${section.label.toLowerCase()}…`}
      />
      <div className="flex justify-end items-center gap-3 mt-2">
        {autoState && <span className="text-[11px]" style={{ color: "var(--text-muted)" }} data-testid={`notes-autosave-${section.slug}`}>{autoState}</span>}
        {!readOnly && (
        <button onClick={saveNotes} disabled={saving || locked} className="btn-secondary text-xs disabled:opacity-50 disabled:cursor-not-allowed" data-testid={`save-notes-${section.slug}`}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Salva note
        </button>
        )}
      </div>

      {module === "installazione" && section.slug === "dichiarazione_conformita" && !readOnly && ["admin", "operatore"].includes(user?.role) && (
        <div className="mt-4 p-3 rounded-lg flex flex-wrap items-center gap-3" style={{ background: "#F3E5F5" }} data-testid="install-dico-ai-box">
          <div className="text-xs flex-1 min-w-[200px]" style={{ color: "#4A148C" }}>L'assistente compila la DiCo dalle note, foto e PDF dell'installazione e dalla chat, e carica qui il PDF (sostituisce quello generato prima).</div>
          <DicoAssistenteButton cid={cid} onCreated={() => onChanged?.()} label="Genera DiCo con assistente" testid="install-dico-ai-btn" />
        </div>
      )}

      {!locked && (
        <div className="mt-4">
          <UploadZone onFiles={handleUpload} />
        </div>
      )}

      {files.length > 0 && (
        <div className="mt-4">
          {!locked && files.length > 1 && (
            <div className="mb-2 text-[11px] flex items-center gap-1" style={{ color: "var(--text-muted)" }} data-testid={`reorder-hint-${section.slug}`}>
              <GripVertical className="w-3 h-3" /> Trascina i file per metterli in ordine cronologico (vale per PDF e link)
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {files.map((f, idx) => {
            const isImage = (f.content_type || "").startsWith("image/");
            const isPdf = (f.content_type || "") === "application/pdf";
            const src = srcOf(f);
            const draggable = !locked && files.length > 1;
            return (
              <div
                key={f.id}
                className="relative group rounded-lg border overflow-hidden transition-shadow"
                style={{
                  borderColor: dragOver === idx ? "var(--primary)" : "var(--border)",
                  background: "var(--surface)",
                  boxShadow: dragOver === idx ? "0 0 0 2px var(--primary)" : undefined,
                  cursor: draggable ? "grab" : undefined,
                }}
                draggable={draggable}
                onDragStart={draggable ? (() => { dragIndex.current = idx; }) : undefined}
                onDragOver={draggable ? ((e) => { e.preventDefault(); if (dragOver !== idx) setDragOver(idx); }) : undefined}
                onDragLeave={draggable ? (() => setDragOver((d) => (d === idx ? null : d))) : undefined}
                onDrop={draggable ? ((e) => { e.preventDefault(); handleDrop(idx); }) : undefined}
                onDragEnd={draggable ? (() => { dragIndex.current = null; setDragOver(null); }) : undefined}
                data-testid={`file-${f.id}`}
              >
                {draggable && (
                  <div
                    className="absolute top-1.5 left-1.5 z-10 p-1 rounded-md bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                    data-testid={`file-drag-handle-${f.id}`}
                  >
                    <GripVertical className="w-3.5 h-3.5" />
                  </div>
                )}
                <span
                  className="absolute bottom-8 left-1.5 z-10 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-black/60 text-white"
                  data-testid={`file-order-${f.id}`}
                >
                  {idx + 1}
                </span>
                {isImage && (
                  <button type="button" onClick={() => photos.open(f.id)} className="block w-full" data-testid={`photo-thumb-${f.id}`}>
                    <img src={src} alt={f.original_filename} className="w-full h-28 object-cover" loading="lazy" draggable={false} />
                  </button>
                )}
                {isPdf && (
                  <button
                    type="button"
                    onClick={() => setPdfPreview({ src, name: f.original_filename })}
                    className="block w-full h-28 relative overflow-hidden bg-[var(--surface-muted)]"
                    data-testid={`pdf-thumb-${f.id}`}
                    title="Apri anteprima PDF"
                  >
                    {/* iframe miniatura non interattiva: mostra la prima pagina */}
                    <iframe
                      src={`${src}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                      title={f.original_filename}
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      style={{ border: 0, transform: "scale(1.05)", transformOrigin: "top center" }}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 flex items-end justify-between bg-gradient-to-t from-black/60 via-transparent to-transparent p-1.5">
                      <span className="text-[10px] uppercase font-semibold text-white">PDF</span>
                      <Eye className="w-3.5 h-3.5 text-white" />
                    </div>
                  </button>
                )}
                {!isImage && !isPdf && (
                  <a href={src} target="_blank" rel="noreferrer" className="flex flex-col items-center justify-center h-28 bg-[var(--surface-muted)] gap-1">
                    <FileText className="w-8 h-8" style={{ color: "var(--primary)" }} />
                    <span className="text-[10px] uppercase">File</span>
                  </a>
                )}
                <div className="p-1.5 text-[11px] truncate" title={f.original_filename}>{f.original_filename}</div>
                {isImage && !locked && (
                  <button
                    onClick={() => setAnnotating(f)}
                    className="absolute top-1.5 right-9 p-1 rounded-md bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`annotate-file-${f.id}`}
                    title="Disegna sulla foto"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
                {!locked && (
                  <button
                    onClick={() => removeFile(f)}
                    className="absolute top-1.5 right-1.5 p-1 rounded-md bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-file-${f.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
          </div>
        </div>
      )}

      {/* Footer: pulsante "Segna come completata" / "Riapri" */}
      {!readOnly && (
      <div className="mt-4 pt-4 border-t flex justify-end" style={{ borderColor: "var(--border)" }}>
        <button
          type="button"
          onClick={toggleCompleted}
          disabled={toggling}
          className={completed ? "btn-secondary" : "btn-primary"}
          data-testid={`btn-complete-${section.slug}`}
        >
          {toggling ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : completed ? (
            <><RotateCcw className="w-4 h-4" /> Sblocca sezione</>
          ) : (
            <><CheckCircle2 className="w-4 h-4" /> Segna come completata</>
          )}
        </button>
      </div>
      )}

      {/* PDF Preview Modal */}
      {pdfPreview && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3"
          onClick={() => setPdfPreview(null)}
          data-testid="pdf-preview-modal"
        >
          <div
            className="bg-white rounded-xl w-full max-w-5xl h-[92vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 shrink-0" style={{ color: "var(--primary)" }} />
                <span className="text-sm font-semibold truncate" title={pdfPreview.name}>{pdfPreview.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={pdfPreview.src}
                  download={pdfPreview.name}
                  className="btn-secondary text-xs"
                  data-testid="pdf-preview-download"
                >
                  <Download className="w-3.5 h-3.5" /> Scarica
                </a>
                <button
                  onClick={() => setPdfPreview(null)}
                  className="p-1.5 rounded hover:bg-black/5"
                  data-testid="pdf-preview-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <iframe
              src={pdfPreview.src}
              title={pdfPreview.name}
              className="flex-1 w-full"
              style={{ border: 0 }}
            />
          </div>
        </div>
      )}
      {photos.ui}
      {annotating && (
        <PhotoAnnotator
          fileId={annotating.id}
          fileName={annotating.original_filename}
          onClose={() => setAnnotating(null)}
          onSaved={(doc) => { setFiles((prev) => prev.map((x) => (x.id === doc.id ? doc : x))); onChanged?.(); }}
        />
      )}
    </div>
  );
}

